import { s } from '@/theme/tokens';
import { FormCard } from '@/ui/composites/FormCard';
import { Inline } from '@/ui/layout/Inline';
import { Dialog, DialogContent, type DialogProps } from '@/ui/primitives';
import type { SpacingToken } from '@/theme/tokens';
import type { SxProps, Theme } from '@/ui/primitives';
import type { KeyboardEventHandler, ReactElement, ReactNode } from 'react';

type DialogLayoutProps = {
  open: boolean;
  title?: string;
  titleSlot?: ReactNode;
  titleSx?: SxProps<Theme>;
  description?: ReactNode;
  actions?: ReactNode;
  padding?: SpacingToken | null;
  headerGap?: SpacingToken;
  actionsAlign?: 'flex-start' | 'center' | 'flex-end' | 'space-between';
  actionsGap?: SpacingToken;
  footerGap?: SpacingToken;
  contentGap?: SpacingToken;
  gap?: SpacingToken;
  dialogSx?: DialogProps['sx'];
  contentSx?: DialogProps['sx'];
  onContentKeyDownCapture?: KeyboardEventHandler<HTMLDivElement>;
  onClose: () => void;
  disableClose?: boolean;
  allowTopBarInteraction?: boolean;
  maxWidth?: DialogProps['maxWidth'];
  children: ReactNode;
};

export const DialogLayout = ({
  open,
  title,
  titleSlot,
  titleSx,
  description,
  actions,
  padding = '2xl',
  headerGap = 'md',
  actionsAlign = 'flex-end',
  actionsGap = 'md',
  footerGap = 'md',
  contentGap = 'md',
  gap = 'lg',
  dialogSx,
  contentSx,
  onContentKeyDownCapture,
  onClose,
  disableClose = false,
  allowTopBarInteraction = false,
  maxWidth = 'sm',
  children,
}: DialogLayoutProps): ReactElement => {
  const baseContentSx: DialogProps['sx'] = {
    overflow: 'visible',
    '& .MuiTypography-body, & .MuiTypography-body1, & .MuiTypography-body2, & .MuiTypography-label':
      {
        fontSize: (theme) => theme.typography.subtitle1.fontSize,
        lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
      },
  };
  const dialogCardSx: SxProps<Theme> | undefined =
    padding === '2xl' ? { pt: s('xl'), pb: s('xl') } : undefined;
  const resolvedContentSx: DialogProps['sx'] = contentSx
    ? ([
        baseContentSx,
        ...(Array.isArray(contentSx) ? contentSx : [contentSx]),
      ] as DialogProps['sx'])
    : baseContentSx;
  const contentSxArray = Array.isArray(resolvedContentSx)
    ? resolvedContentSx
    : ([resolvedContentSx] as Array<DialogProps['sx']>);
  const footer = actions ? (
    <Inline gap={actionsGap} justify={actionsAlign}>
      {actions}
    </Inline>
  ) : undefined;
  const dialogSlotProps: DialogProps['slotProps'] = disableClose
    ? {
        paper: { sx: { p: s('none') } },
        backdrop: {
          onClick: (event) => {
            event.stopPropagation();
          },
        },
      }
    : { paper: { sx: { p: s('none') } } };
  const topBarOffsetSx: DialogProps['sx'] | undefined = allowTopBarInteraction
    ? {
        zIndex: 9,
        '& .MuiBackdrop-root': {
          top: 'var(--app-topbar-height, 54px)',
          height: 'calc(100% - var(--app-topbar-height, 54px))',
        },
        '& .MuiDialog-container': {
          top: 'var(--app-topbar-height, 54px)',
          height: 'calc(100% - var(--app-topbar-height, 54px))',
          maxHeight: 'calc(100% - var(--app-topbar-height, 54px))',
        },
      }
    : undefined;
  const hasDialogSx = Boolean(topBarOffsetSx) || Boolean(dialogSx);
  const resolvedDialogSx = hasDialogSx
    ? ([topBarOffsetSx, dialogSx].filter(Boolean) as DialogProps['sx'])
    : undefined;

  return (
    <Dialog
      open={open}
      onClose={disableClose ? undefined : onClose}
      disableEscapeKeyDown={disableClose}
      fullWidth
      maxWidth={maxWidth}
      slotProps={dialogSlotProps}
      sx={resolvedDialogSx}
    >
      <DialogContent
        sx={[{ p: s('none') }, ...contentSxArray]}
        onKeyDownCapture={onContentKeyDownCapture}
      >
        <FormCard
          container="none"
          title={title}
          titleSlot={titleSlot}
          titleSx={titleSx}
          description={description}
          padding={padding}
          sx={dialogCardSx}
          headerGap={headerGap}
          footer={footer}
          footerGap={footerGap}
          contentGap={contentGap}
          gap={gap}
        >
          {children}
        </FormCard>
      </DialogContent>
    </Dialog>
  );
};
